import { z } from "zod";

import apiClient from "../../../shared/api/client";

import type { ListObjectsResponse } from "../types";

const s3ObjectSchema = z.object({
  key: z.string(),

  size: z.number(),

  lastModified: z.string().nullable(),

  etag: z.string().nullable(),
});

const s3FolderSchema = z.object({
  prefix: z.string(),
});

const listObjectsSchema = z.object({
  objects: z.array(s3ObjectSchema),

  folders: z.array(s3FolderSchema),

  bucket: z.string(),

  prefix: z.string(),
});

const bucketsSchema = z.array(z.string());

export const fetchBuckets = async (): Promise<string[]> => {
  const response = await apiClient.get("/admin/storage/buckets");

  return bucketsSchema.parse(response.data.data);
};

export const fetchObjects = async (
  bucket: string,

  prefix?: string,
): Promise<ListObjectsResponse> => {
  const response = await apiClient.get(
    `/admin/storage/buckets/${bucket}/objects`,

    {
      params: { prefix },
    },
  );

  return listObjectsSchema.parse(response.data.data);
};

export const deleteObject = async (
  bucket: string,

  key: string,
): Promise<void> => {
  await apiClient.delete(`/admin/storage/buckets/${bucket}/objects`, {
    params: { key },
  });
};

export const uploadFile = async (
  bucket: string,

  prefix: string,

  file: File,

  onProgress?: (progress: number) => void,
): Promise<{ key: string; size: number; bucket: string }> => {
  const formData = new FormData();

  formData.append("file", file);

  const response = await apiClient.post(
    `/admin/storage/buckets/${bucket}/upload`,

    formData,

    {
      params: { prefix },

      headers: { "Content-Type": "multipart/form-data" },

      onUploadProgress: (event) => {
        if (event.total && onProgress) {
          onProgress(Math.round((event.loaded * 100) / event.total));
        }
      },
    },
  );

  return response.data.data;
};

export const createFolder = async (
  bucket: string,

  path: string,
): Promise<{ key: string }> => {
  const response = await apiClient.post(
    `/admin/storage/buckets/${bucket}/folder`,

    null,

    {
      params: { path },
    },
  );

  return response.data;
};

export const deleteRecursive = async (
  bucket: string,

  prefix: string,
): Promise<{ deleted: number }> => {
  const response = await apiClient.delete(
    `/admin/storage/buckets/${bucket}/objects/recursive`,

    {
      params: { prefix },
    },
  );

  return response.data;
};

export const storageBrowserApi = {
  fetchBuckets,

  fetchObjects,

  deleteObject,

  deleteRecursive,

  uploadFile,

  createFolder,
};
